import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Download, MessageSquare, Mail, Plus, History } from "lucide-react";

/**
 * BillPreviewModal — renders a print-quality IPD bill.
 *
 * Bill payload arrives from `/api/ipd/billing/{admission_id}` already
 * enriched with patient_name, doctor_name, ward_name, bed_number, so no
 * follow-up joins are needed. We do still fetch `/org/me` for the
 * hospital header (address, phone, email, registration number).
 *
 * Print / PDF strategy: open a brand-new `window.open()` popup with a
 * self-contained HTML document. This avoids Radix Dialog portal issues
 * and Tailwind's `@media print` reset quirks — the popup renders
 * predictably in every browser and users choose "Save as PDF" or an
 * installed printer from the native dialog.
 * SMS / Email: mocked toast until Twilio / Resend keys land.
 */
export default function BillPreviewModal({ open, onClose, admissionId }) {
  const [bill, setBill] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const printRef = useRef(null);
  const { user } = useAuth();

  // Resolve a display-ready hospital record, falling back to whatever
  // the authenticated user knows about their org. This guarantees the
  // bill header never renders an empty "Hospital Name" placeholder for
  // brand-new accounts that haven't filled in Settings → Org yet.
  const org = hospital || {};
  const rawLogo = org.org_image || user?.org_image || "";
  const resolvedLogo = /^(https?:|data:)/i.test(rawLogo) ? rawLogo : fileUrl(rawLogo);
  const orgResolved = {
    org_name: org.org_name || user?.org_name || "Your Hospital",
    address: org.address || org.org_address || "",
    city: org.city || "",
    mobile: org.mobile || org.org_phone || "",
    email: org.email || user?.email || "",
    registration_no: org.registration_no || org.license_number || "",
    logo_url: resolvedLogo,
  };

  useEffect(() => {
    if (!open || !admissionId) return;
    (async () => {
      setLoading(true); setError(null);
      try {
        const [b, orgRes] = await Promise.all([
          api.get(`/ipd/billing/${admissionId}`),
          api.get(`/org/me`).catch(() => ({ data: null })),
        ]);
        setBill(b.data);
        setHospital(orgRes.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Bill not found — create one from the Billing tab first.");
      } finally { setLoading(false); }
    })();
  }, [open, admissionId]);

  const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  /** Opens a new window with a self-contained bill document and calls print. */
  const handlePrint = () => {
    const scope = printRef.current;
    if (!scope) { toast.error("Bill not ready for print"); return; }
    const printWindow = window.open("", "_blank", "width=900,height=800");
    if (!printWindow) {
      toast.error("Pop-up blocked — allow pop-ups for this site to print");
      return;
    }
    const billHTML = scope.innerHTML;
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>IPD Bill · ${(bill?.id || "").slice(0, 8).toUpperCase()}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box;
        -webkit-print-color-adjust: exact; color-adjust: exact;
        print-color-adjust: exact; }
    body { font-family: Georgia, "Times New Roman", serif; font-size: 12px;
           line-height: 1.5; color: #000; background: #fff; padding: 24px; }
    h1 { font-size: 22px; margin-bottom: 4px; }
    h2 { font-size: 15px; margin: 12px 0 4px; }
    p  { margin: 2px 0; }
    hr { border: 0; border-top: 1px solid #ccc; margin: 12px 0; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    th, td { border: 1px solid #333; padding: 6px 8px; text-align: left; }
    th { background: #f0f0f0; font-weight: bold; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .header { text-align: center; border-bottom: 2px solid #000;
              padding-bottom: 8px; margin-bottom: 12px; }
    .label { font-size: 10px; text-transform: uppercase;
             letter-spacing: 1.5px; color: #555; }
    .amount { text-align: right; font-weight: bold; }
    .total-row td { border-top: 2px solid #000; font-weight: bold; font-size: 14px; }
    .footer { text-align: center; margin-top: 24px; padding-top: 8px;
              border-top: 1px solid #ccc; font-size: 10px; color: #666; }
    @page { size: A4; margin: 12mm; }
    @media print {
      body { padding: 0; }
      * { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  ${billHTML}
</body>
</html>`);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 300);
  };

  const handleSms = () => toast.success("SMS queued — mock (wire Twilio / MSG91 keys)");
  const handleEmail = () => toast.success("Email queued — mock (wire Resend / SendGrid keys)");

  if (!admissionId) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0"
                     data-testid="ipd-bill-preview-modal">
        <DialogHeader className="px-6 pt-5 pb-2 border-b flex-row justify-between items-start no-print">
          <DialogTitle>Bill Preview</DialogTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handlePrint} data-testid="bill-print">
              <Printer size={14} className="mr-1" /> Print
            </Button>
            <Button size="sm" variant="outline" onClick={handlePrint} data-testid="bill-pdf">
              <Download size={14} className="mr-1" /> PDF
            </Button>
            <Button size="sm" variant="outline" onClick={handleSms} data-testid="bill-sms">
              <MessageSquare size={14} className="mr-1" /> SMS
            </Button>
            <Button size="sm" variant="outline" onClick={handleEmail} data-testid="bill-email">
              <Mail size={14} className="mr-1" /> Email
            </Button>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading bill…</div>
        ) : error ? (
          <div className="p-8 text-center text-slate-500">{error}</div>
        ) : !bill ? (
          <div className="p-8 text-center text-slate-500">No bill available for this admission.</div>
        ) : (
          <div ref={printRef} className="bill-print-scope p-8 font-serif text-slate-900">
            {/* Header — hospital */}
            <div className="header border-b-2 border-slate-800 pb-3 mb-4 flex items-center gap-4">
              {orgResolved.logo_url ? (
                <img src={orgResolved.logo_url} alt="Hospital logo"
                     className="w-16 h-16 object-contain shrink-0"
                     data-testid="bill-hospital-logo" />
              ) : <div className="w-16 shrink-0" />}
              <div className="flex-1 text-center">
                <p className="text-[10px] tracking-[0.3em] text-slate-500 uppercase mb-1">UpchaarQ Healthcare</p>
                <h1 className="text-2xl font-bold">
                  {orgResolved.org_name}
                </h1>
                <p className="text-xs mt-1">
                  {orgResolved.address}
                  {orgResolved.city ? `, ${orgResolved.city}` : ""}
                </p>
                <p className="text-xs">
                  {orgResolved.mobile && <>Ph: {orgResolved.mobile}</>}
                  {orgResolved.email && <> · {orgResolved.email}</>}
                  {orgResolved.registration_no && <> · Reg: {orgResolved.registration_no}</>}
                </p>
              </div>
              <div className="w-16 shrink-0" />
            </div>

            {/* Bill title band */}
            <div className="grid2 grid grid-cols-2 gap-3 text-xs mb-3">
              <div>
                <h2 className="font-bold uppercase tracking-wider">IPD Bill</h2>
                <p><strong>Bill No:</strong> {bill.bill_no || (bill.id || "").slice(0, 8).toUpperCase()}</p>
                <p><strong>Bill Date:</strong> {(bill.created_at || "").slice(0, 10)}</p>
                <p><strong>Period:</strong> —</p>
              </div>
              <div className="text-right">
                <p>
                  <strong>Status:</strong>{" "}
                  <span style={{
                    color: bill.payment_status === "paid" ? "#047857"
                         : bill.payment_status === "partial" ? "#b45309"
                         : "#b91c1c",
                  }}>
                    {(bill.payment_status || "pending").toUpperCase()}
                  </span>
                </p>
                <p><strong>Method:</strong> {bill.payment_method || "—"}</p>
              </div>
            </div>

            <hr />

            {/* Patient + Admission */}
            <div className="grid2 grid grid-cols-2 gap-4 text-xs mt-3">
              <div>
                <p className="label">Patient Details</p>
                <p><strong>{bill.patient_name || "—"}</strong></p>
                <p>Age: {bill.patient_age ?? "—"}  ·  {bill.patient_gender || "—"}</p>
                <p>Phone: {bill.patient_mobile || "—"}</p>
                <p className="text-[10px] text-slate-500 mt-1">
                  ID: <span className="font-mono">{(bill.patient_id || "").slice(0, 8)}</span>
                </p>
              </div>
              <div>
                <p className="label">Admission Details</p>
                <p>Doctor: <strong>{bill.doctor_name || "—"}</strong></p>
                {bill.doctor_nmc && <p className="text-[10px] text-slate-500">NMC {bill.doctor_nmc}</p>}
                <p>Ward: {bill.ward_name || "—"}</p>
                <p>Bed: <span className="font-mono">{bill.bed_number || "—"}</span></p>
                <p>Days: {bill.bed_charges?.days || 0}  ·  Rate: {money(bill.bed_charges?.rate_per_day)}/day</p>
              </div>
            </div>

            {/* Charges */}
            <h2 className="mt-6 font-bold uppercase tracking-wider text-sm">Charges</h2>
            <table className="mt-2 text-sm">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="amount" style={{ textAlign: "right" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Accommodation ({bill.bed_charges?.days || 0} × {money(bill.bed_charges?.rate_per_day)})</td>
                  <td className="amount">{money(bill.bed_charges?.total)}</td>
                </tr>
                <tr><td>Medicines</td>       <td className="amount">{money(bill.medication_charges)}</td></tr>
                <tr><td>Procedures / Tests</td><td className="amount">{money(bill.procedure_charges)}</td></tr>
                <tr><td>Consultation</td>    <td className="amount">{money(bill.consultation_charges)}</td></tr>
                <tr><td>Other</td>           <td className="amount">{money(bill.other_charges)}</td></tr>
                {bill.discount_applied > 0 && (
                  <tr style={{ color: "#b91c1c" }}>
                    <td>Discount</td>
                    <td className="amount">− {money(bill.discount_applied)}</td>
                  </tr>
                )}
                <tr className="total-row">
                  <td>Total Due</td>
                  <td className="amount">{money(bill.total_amount)}</td>
                </tr>
                <tr style={{ color: "#047857" }}>
                  <td>Amount Paid</td>
                  <td className="amount">{money(bill.amount_paid)}</td>
                </tr>
                <tr style={{ color: "#b91c1c" }}>
                  <td>Balance</td>
                  <td className="amount">
                    {money(Math.max(0, (bill.total_amount || 0) - (bill.amount_paid || 0)))}
                  </td>
                </tr>
              </tbody>
            </table>

            {bill.insurance_claim_id && (
              <div className="mt-3 text-xs" style={{ padding: 6, background: "#f0f9ff",
                                                       border: "1px solid #bae6fd", borderRadius: 4 }}>
                <strong>Insurance claim:</strong> {bill.insurance_claim_id}
              </div>
            )}

            {/* Payment history + Record Payment (Item 2) */}
            <PaymentHistoryBlock bill={bill} onRecorded={(row) => setBill(row)} />

            <div className="footer">
              Generated by <strong>UpchaarQ</strong> · https://upchaarq.com · support@upchaarq.com
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Payment history block + inline "Record Payment" form              */
/* ------------------------------------------------------------------ */

function PaymentHistoryBlock({ bill, onRecorded }) {
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  const history = bill?.payment_history || [];
  const balance = Math.max(0, (bill?.total_amount || 0) - (bill?.amount_paid || 0));
  const done = balance === 0 && (bill?.total_amount || 0) > 0;

  const submit = async () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Enter a positive amount"); return; }
    if (n > balance) { toast.error(`Amount can't exceed balance ${money(balance)}`); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/ipd/billing/${bill.admission_id}/payment`, {
        amount_paid: n, payment_method: method, insurance_claim_id: note || null,
      });
      toast.success(`Payment recorded ${money(n)}`);
      setAmount(""); setNote(""); setMethod("cash"); setShowForm(false);
      onRecorded?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-5 no-print-nothing" data-testid="bill-payment-history">
      <div className="flex items-center justify-between mt-4 pt-3 border-t">
        <p className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1">
          <History className="w-3 h-3" /> Payment History
        </p>
        {!done && (
          <Button size="sm" variant="outline" onClick={() => setShowForm((s) => !s)}
                  className="no-print" data-testid="bill-record-payment-btn">
            <Plus size={12} className="mr-1" /> Record Payment
          </Button>
        )}
      </div>

      {history.length === 0 ? (
        <p className="text-xs text-slate-500 mt-2">No payments recorded yet.</p>
      ) : (
        <table className="mt-2 text-xs" data-testid="bill-payment-history-table">
          <thead>
            <tr>
              <th>Date</th><th>Method</th><th className="amount" style={{textAlign:"right"}}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id || h.recorded_at}>
                <td>{(h.recorded_at || "").slice(0, 16).replace("T", " ")}</td>
                <td>{h.payment_method || "—"}</td>
                <td className="amount" style={{textAlign:"right"}}>{money(h.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && !done && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-2 no-print p-3 border border-slate-200 rounded bg-slate-50">
          <div>
            <Label className="text-[10px]">Amount (max {money(balance)})</Label>
            <Input type="number" min="1" max={balance} value={amount}
                   onChange={(e) => setAmount(e.target.value)}
                   data-testid="bill-record-amount" />
          </div>
          <div>
            <Label className="text-[10px]">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="bill-record-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="insurance">Insurance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder="e.g. claim id" maxLength={120} />
          </div>
          <div className="flex items-end">
            <Button onClick={submit} disabled={saving || !amount}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="bill-record-submit">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

